import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { unitHistoryApi } from '../lib/unit-history-api';
import { UnitHistoryTimeline } from '../components/UnitHistory/UnitHistoryTimeline';
import { PriceTrendChart } from '../components/UnitHistory/PriceTrendChart';
import { MetricCard } from '../components/UnitHistory/MetricCard';
import { 
  ChartBarIcon, 
  CurrencyEuroIcon, 
  ClockIcon, 
  CheckCircleIcon 
} from '@heroicons/react/24/outline';

export const UnitAnalytics: React.FC = () => {
  const { unitId } = useParams<{ unitId: string }>();

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['unit-history', unitId],
    queryFn: () => unitHistoryApi.getUnitHistory(parseInt(unitId!)),
    enabled: !!unitId
  });

  const priceTrend = useMemo(() => {
    return history
      ?.filter(h => h.eventType === 'price_change')
      .map(h => ({
        date: h.eventDate,
        price: h.price!,
        change: h.priceChangePercentage || 0,
        unitRef: `PF${unitId}`
      })) || [];
  }, [history, unitId]);

  const metrics = useMemo(() => {
    if (!history || history.length === 0) return [];

    const latestEvent = history[0];
    const priceChanges = history.filter(h => h.eventType === 'price_change').length;
    const statusChanges = history.filter(h => h.eventType === 'status_change').length;

    return [
      {
        title: 'Days on Market',
        value: latestEvent.daysOnMarket || 0,
        subtitle: 'Current listing period',
        icon: ClockIcon
      },
      {
        title: 'Price Changes',
        value: priceChanges,
        subtitle: 'Total price updates',
        icon: CurrencyEuroIcon
      },
      {
        title: 'Status Changes',
        value: statusChanges,
        subtitle: 'Status updates',
        icon: CheckCircleIcon
      },
      {
        title: 'Current Status',
        value: latestEvent.status,
        subtitle: 'Latest status',
        icon: CheckCircleIcon
      }
    ];
  }, [history]);

  if (historyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 via-tagus-navy to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.2
        }}></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
          <div className="flex items-center justify-center sm:justify-start mb-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center mr-3">
              <ChartBarIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                Unit Analytics - PF{unitId}
              </h1>
            </div>
          </div>
          <p className="text-primary-100 text-sm sm:text-base">
            Track price changes, status updates, and market performance
          </p>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {metrics.map((metric, index) => (
          <MetricCard
            key={index}
            title={metric.title}
            value={metric.value}
            subtitle={metric.subtitle}
            icon={metric.icon}
          />
        ))}
      </div>

      {/* Price Trend Chart */}
      <div className="bg-white p-6 rounded-xl shadow-tagus border border-secondary-100">
        <h3 className="text-lg font-semibold mb-4 text-secondary-900">Price Evolution</h3>
        <PriceTrendChart data={priceTrend} />
      </div>

      {/* Unit History Timeline */}
      <div className="bg-white p-6 rounded-xl shadow-tagus border border-secondary-100">
        <h3 className="text-lg font-semibold mb-4 text-secondary-900">Unit History</h3>
        <UnitHistoryTimeline events={history || []} />
      </div>
    </div>
  );
};
